import { type Moment, moment } from "../date";
import type { Enums, Nullable } from "../types";
import type { AttendanceListItem } from "../types/attendances";

/**
 * The seconds a set of attendances of one type adds up to: the time worked for `work`, the rest
 * taken for `break`.
 *
 * A rejected entry is time nobody is credited with, so it adds nothing. An entry still running
 * counts up to `now`, and without a `now` it adds nothing — which is how a figure that is not
 * re-rendered every second leaves the running entry out rather than freezing it at a stale value.
 *
 * @param attendances - The entries to add up, typically one person's day.
 * @param type - Which kind of entry counts.
 * @param now - The moment a running entry is counted up to, or `null` to leave it out.
 * @returns The total in whole seconds.
 */
export function getAttendancesSeconds(
	attendances: ReadonlyArray<Pick<AttendanceListItem, "type" | "state" | "startTime" | "endTime">>,
	type: Enums<"attendanceType">,
	now: Nullable<Moment> = null,
): number {
	let seconds = 0;
	for (const attendance of attendances) {
		if (attendance.type !== type) continue;
		if (attendance.state === "rejected") continue;
		if (!attendance.startTime) continue;
		const end = attendance.endTime ? moment(attendance.endTime) : now;
		if (!end) continue;
		seconds += Math.max(0, end.diff(moment(attendance.startTime), "seconds"));
	}
	return seconds;
}
